<!-- Основная боковая навигация приложения. -->
<template>
  <aside
    class="app-sidebar"
    :class="{ 'sidebar-collapsed': collapsed }"
  >
    <BaseContextMenu
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :items="contextMenu.items"
      @close="closeContextMenu"
    />

    <div class="sidebar-header">
      <div
        class="header-logo"
        @click="$router.push({ name: 'Home' })"
      >
        <AppLogo />
      </div>
      <div
        class="collapse-header-btn desktop-only"
        v-if="!collapsed"
        @click.stop="$emit('toggle-collapse')"
        title="Свернуть/Развернуть"
      >
        <svg
          :class="{ rotated: collapsed }"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 18L9 12L15 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <!-- Прокрутка меню не затрагивает логотип и шапку сайдбара. -->
    <div class="sidebar-content">
      <!-- Боковая панель | Основные пункты меню -->
      <AppSidebarItems
        :collapsed="collapsed"
        :active-item="activeItem"
        :permissions-loaded="permissionsLoaded"
        :can-see-page="canSeePage"
        :unread-chats-count="unreadChatsCount"
        @toggle-collapse="$emit('toggle-collapse')"
        @navigate="navigate"
        @middle-click="handleMiddleClick"
        @context-menu="showContextMenu"
      />

      <!-- Боковая панель | Нижняя часть -->
      <div class="sidebar-footer">
        <AppSidebarFooterItems
          :collapsed="collapsed"
          :active-item="activeItem"
          :can-see-page="canSeePage"
          @navigate="navigate"
          @middle-click="handleMiddleClick"
          @context-menu="showContextMenu"
        />

        <base-loader v-if="dataIsLoading" />
        <div class="sidebar-user-block mobile-only" v-else>
          <span class="user-initials">{{ userInitials }}</span>
          <base-button class="user-btn" @click="goToUserProfile">
            Профиль
          </base-button>
          <base-button class="user-btn-exit" @click="logout">
            Выйти
          </base-button>
        </div>
      </div>
    </div>
  </aside>
</template>

<script type="text/javascript">
import { mapState, mapActions } from "pinia";

import AppSidebarFooterItems from "@/components/AppSidebar/AppSidebarFooterItems.vue";
import AppSidebarItems from "@/components/AppSidebar/AppSidebarItems.vue";
import AppLogo from "@/components/AppLogo.vue";
import { useUserDataStore } from "@/stores/userDataStore";
import { usePermissionsStore } from "@/stores/permissionsStore";
import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { PAGE_ACCESS_KEYS } from "@/utils/routePermissions";

const SIDEBAR_ITEM_BY_PAGE_KEY = {
  activity_logs: "activity-log",
  company_users: "users",
};

const SIDEBAR_ITEM_BY_ROUTE = {
  Fleet: "fleet",
  Chats: "chats",
};

export default {
  name: "app-sidebar",
  components: {
    AppLogo,
    AppSidebarItems,
    AppSidebarFooterItems,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    mobileOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeItem: "home",
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        items: [],
      },
    };
  },
  watch: {
    $route() {
      this.updateActiveItem();
    },
  },
  computed: {
    ...mapState(useUserDataStore, {
      userInitials: "initials",
      dataIsLoading: "isLoading",
    }),
    ...mapState(usePermissionsStore, ["canSeePage"]),
    ...mapState(useChatStore, {
      unreadChatsCount: "unreadMessagesCount",
    }),
    permissionsLoaded() {
      return usePermissionsStore().isLoaded;
    },
  },
  methods: {
    updateActiveItem() {
      const routeName = this.$route.name;
      const pageKey = PAGE_ACCESS_KEYS[routeName];

      this.activeItem =
        SIDEBAR_ITEM_BY_ROUTE[routeName] ||
        SIDEBAR_ITEM_BY_PAGE_KEY[pageKey] ||
        pageKey ||
        "home";
    },

    navigate(event, routeName) {
      if (event.ctrlKey || event.metaKey) {
        const routeData = this.$router.resolve({ name: routeName });
        window.open(routeData.href, "_blank");
        return;
      }
      this.$router.push({ name: routeName });
    },

    handleMiddleClick(event, routeName) {
      if (event.button === 1) {
        event.preventDefault();
        const routeData = this.$router.resolve({ name: routeName });
        window.open(routeData.href, "_blank");
      }
    },

    // TODO: вынести общую механику context menu в helper/composable.
    showContextMenu(event, routeName) {
      event.stopPropagation();

      const items = [
        {
          key: "open-new-tab",
          label: "Открыть в новой вкладке",
          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>`,
          action: () => {
            const routeData = this.$router.resolve({
              name: routeName,
            });
            window.open(routeData.href, "_blank");
          },
        },
      ];

      const menuWidth = 220;
      const menuHeight = items.length * 44;
      let x = event.clientX;
      let y = event.clientY;

      if (x + menuWidth > window.innerWidth)
        x = window.innerWidth - menuWidth - 8;
      if (y + menuHeight > window.innerHeight)
        y = window.innerHeight - menuHeight - 8;

      this.contextMenu = {
        visible: true,
        x,
        y,
        items,
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },

    goToUserProfile() {
      this.$router.push({ name: "UserProfile" });
    },

    ...mapActions(useAuthStore, { authLogout: "logout" }),

    logout() {
      this.authLogout();
      this.$router.push({ name: "Login" });
    },
  },
  mounted() {
    this.updateActiveItem();
    document.addEventListener("click", this.closeContextMenu);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeContextMenu);
  },
};
</script>

<style scoped>
.app-sidebar {
  background-color: var(--color-dark);
  color: var(--color-light);
  padding: 0 15px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;
}

.app-sidebar:hover {
  background-color: #2a2a40;
}

.mobile-only {
  display: none;
}

.app-sidebar.sidebar-collapsed .header-logo {
  margin-left: 0;
}

.app-sidebar.sidebar-collapsed .header-logo svg {
  width: 50px;
  height: 50px;
}

.app-sidebar.sidebar-collapsed {
  padding: 0 10px;
}

.app-sidebar.sidebar-collapsed :deep(.nav-link) {
  display: none;
}

.app-sidebar.sidebar-collapsed .sidebar-header {
  justify-content: center;
  padding: 0;
}

.app-sidebar.sidebar-collapsed :deep(.nav-item) {
  justify-content: center;
}

.app-sidebar.sidebar-collapsed :deep(.link-icon_box) {
  margin-left: 0;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  margin-right: -15px;
  padding-right: 15px;
  overflow-y: auto;
  overflow-x: hidden;
}

.app-sidebar.sidebar-collapsed .sidebar-content {
  margin-right: -10px;
  padding-right: 10px;
}

.sidebar-content::-webkit-scrollbar {
  width: 6px;
}
.sidebar-content::-webkit-scrollbar-track {
  background: var(--color-dark);
}
.sidebar-content::-webkit-scrollbar-thumb {
  background: #4a4a6a;
  border-radius: 3px;
}
.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #5a5a7a;
}

.sidebar-header {
  position: relative;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #2c3e50;
  flex-shrink: 0;
  overflow: hidden;
}

.header-logo {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-left: 10px;
  color: var(--color-green);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

:deep(.collapse-btn),
.collapse-header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  color: var(--color-light);
  border-radius: 6px;
  transition: background-color 0.3s ease;
}

:deep(.collapse-btn) {
  align-self: center;
  rotate: 180deg;
}

.collapse-header-btn {
  position: absolute;
  bottom: 25px;
  right: 10px;
  margin-bottom: 0;
}

:deep(.collapse-btn:hover),
.collapse-header-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

:deep(.collapse-btn svg),
.collapse-header-btn svg {
  transition: transform 0.3s ease;
}

.app-nav {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-top: var(--base-in-indent);
}

:deep(.nav-list) {
  list-style: none;
  margin: 0;
  padding: 0;
}

:deep(.nav-item) {
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
  background-color: transparent;
  border: none;
  text-align: left;
  font-size: 18px;
  color: var(--color-light);
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
}

:deep(.nav-item:hover),
:deep(.nav-item:active) {
  background-color: rgba(255, 255, 255, 0.1);
}

:deep(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

:deep(.link-icon_box path),
:deep(.link-icon_box rect),
:deep(.link-icon_box polygon),
:deep(.link-icon_box circle) {
  fill: var(--color-green);
}

:deep(.driver-svg rect),
:deep(.driver-svg circle),
:deep(.driver-svg path),
:deep(.driver-svg line) {
  fill: none;
  stroke: var(--color-green);
}

:deep(.integrations-svg path) {
  fill: none;
  stroke: var(--color-green);
}

:deep(.activity-log-svg path) {
  fill: none;
  stroke: var(--color-green);
}

:deep(.collapse-btn path) {
  fill: none;
  stroke: #cfcfcf;
}
.collapse-header-btn path {
  fill: none;
  stroke: #cfcfcf;
}

:deep(.vehicles__icon) {
  transform: translateY(-4px);
}

:deep(.nav-link) {
  display: block;
  padding: 12px 20px 12px 10px;
  color: var(--color-light);
  text-decoration: none;
  user-select: none;
}

.sidebar-footer {
  padding: 20px 0;
  border-top: 1px solid #2c3e50;
  flex-shrink: 0;
}

:deep(.settings__link) {
  margin-bottom: 0 !important;
}

:deep(.link-icon_box) {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}

.sidebar-user-block {
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #2c3e50;
}

.user-btn-exit {
  padding: 10px !important;
  background-color: var(--color-danger) !important;
  border: none !important;
  color: #fff !important;
}

.user-btn-exit:hover {
  background-color: var(--color-danger-hover) !important;
}

:deep(.nav-skeleton) {
  pointer-events: none;
}

:deep(.nav-item--skeleton) {
  gap: 10px;
  padding: 8px;
  cursor: default;
}

:deep(.nav-item--skeleton:hover) {
  background-color: transparent;
}

:deep(.skeleton-icon),
:deep(.skeleton-label) {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: sidebar-shimmer 1.5s ease-in-out infinite;
  border-radius: 6px;
}

:deep(.skeleton-icon) {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}

:deep(.skeleton-label) {
  height: 16px;
  width: 90px;
}

@keyframes sidebar-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@media (max-width: 1024px) {
  .desktop-only,
  :deep(.desktop-only) {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  :deep(.nav-item) {
    margin-bottom: 10px;
    font-size: 16px;
  }

  :deep(.nav-link) {
    padding: 10px 16px 10px 10px;
  }
}
</style>
